import {
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Heading,
  Link,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MetriportMedicalApi } from "@metriport/api-sdk";
import axios from "axios";
import { getOSSBaseURL } from "../../api/apiOSS";
import { useAppContext } from "../contexts/app";
import { AppState } from "../contexts/app/reducer";
import { MedicalAppStateProvider } from "../contexts/medical";
import { isSandbox } from "../shared/util";
import MedicalDash from "./dash";

export default function Medical() {
  const { state } = useAppContext();

  const apiKeyGenerated = state.keys?.apiKey != null && !!state.keys?.apiKey.length;

  if (state.isLoaded && !apiKeyGenerated) {
    return (
      <VStack p={10} align="start">
        <Card h={"100%"} boxShadow={"md"} borderRadius={20} bgColor={"red.200"} maxW={"100%"}>
          <CardBody p={8}>
            <Stack spacing="4">
              <Heading color={"red.700"} size="md">
                No API Key
              </Heading>
              <Text color={"red.700"} fontSize={18}>
                To get started with the Medical API, first go to the{" "}
                <Link href="/developers" textDecoration={"underline"}>
                  Developers
                </Link>{" "}
                page to generate an API key.
              </Text>
            </Stack>
          </CardBody>
        </Card>
      </VStack>
    );
  }

  if (state.isLoaded && apiKeyGenerated && !isMedicalActive(state)) {
    return (
      <VStack p={10} align="start">
        <Card h={"100%"} boxShadow={"md"} borderRadius={20} maxW={"100%"}>
          <CardBody p={8}>
            <Stack spacing="4">
              <Heading size="md">Request access</Heading>
              <Text fontSize={18}>
                To access the Medical API, you must first go to the{" "}
                <Link href="/billing" textDecoration={"underline"}>
                  Products & Billing
                </Link>{" "}
                page and select 'Get Started' on the Medical API.
              </Text>
            </Stack>
          </CardBody>
        </Card>
      </VStack>
    );
  }

  if (state.isLoaded && state.keys?.apiKey != null && !!state.keys?.apiKey.length) {
    const headers: Record<string, string> = {
      [MetriportMedicalApi.headers.clientApp]: "Metriport-Dashboard",
    };

    const timeoutMillis = 29000;
    // Verbose so we use more combinations of the SDK's constructor
    const options = isSandbox()
      ? {
          axios,
          sandbox: true,
          timeout: timeoutMillis,
          ...headers,
        }
      : {
          axios,
          baseAddress: getOSSBaseURL(),
          timeout: timeoutMillis,
          ...headers,
        };
    const metriportAPI = new MetriportMedicalApi(state.keys.apiKey, options);

    return (
      <>
        {isSandbox() && (
          <Alert status="info" mb={50}>
            <AlertIcon />
            The sandbox mode allows you to try out the Medical API with some example de-identified
            clinical data. To learn more, please visit our&nbsp;
            <a href="https://docs.metriport.com/medical-api/more-info/sandbox" target="_blank">
              <u>
                <b>documentation</b>
              </u>
            </a>
            .
          </Alert>
        )}
        <VStack p={10} align="start">
          <MedicalAppStateProvider>
            <MedicalDash api={metriportAPI}></MedicalDash>
          </MedicalAppStateProvider>
        </VStack>
      </>
    );
  }

  return null;
}

export const isMedicalActive = (state: AppState): boolean => {
  if (isSandbox()) return true;

  return !!state.customer?.medicalStripeSubscriptionItemId && !!state.customer?.subscriptionStatus;
};
